import React from "react";
import { AlertCircle, Clock, XCircle, CheckCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

export const MembershipBanner = ({ membership, onUpgrade }) => {
  if (!membership) return null;
  
  const { status, message, hours_remaining, days_remaining, plan } = membership;
  
  // Don't show banner for active accounts without warnings
  if (status === "active" && !message) return null;
  
  // Determine banner style and icon based on status
  const getBannerStyle = () => {
    switch (status) {
      case "pending_approval":
        return {
          bg: "bg-amber-50 border-amber-200",
          text: "text-amber-800",
          icon: <Clock className="w-5 h-5 text-amber-600" />,
          showUpgrade: false
        };
      case "grace_period":
        return {
          bg: "bg-red-50 border-red-200",
          text: "text-red-800",
          icon: <AlertCircle className="w-5 h-5 text-red-600 animate-pulse" />,
          showUpgrade: true
        };
      case "expiring_soon":
        return {
          bg: "bg-yellow-50 border-yellow-200",
          text: "text-yellow-800",
          icon: <AlertTriangle className="w-5 h-5 text-yellow-600" />,
          showUpgrade: true
        };
      case "inactive":
        return {
          bg: "bg-red-100 border-red-300",
          text: "text-red-900",
          icon: <XCircle className="w-5 h-5 text-red-700" />,
          showUpgrade: true
        };
      case "rejected":
        return {
          bg: "bg-gray-100 border-gray-300",
          text: "text-gray-800",
          icon: <XCircle className="w-5 h-5 text-gray-600" />,
          showUpgrade: false
        };
      default:
        return null;
    }
  };
  
  const style = getBannerStyle();
  if (!style) return null;
  
  // Format time remaining
  const formatTimeRemaining = () => {
    if (hours_remaining !== undefined && hours_remaining <= 24) {
      return `${hours_remaining}h restantes`;
    }
    if (days_remaining !== undefined) {
      return `${days_remaining} días restantes`;
    }
    return null;
  };
  
  const timeText = formatTimeRemaining();
  
  return (
    <div 
      className={`${style.bg} border rounded-xl p-4 mb-6 flex items-start gap-4`}
      data-testid="membership-banner"
    >
      <div className="flex-shrink-0 mt-0.5">
        {style.icon}
      </div>
      
      <div className="flex-1">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className={`font-medium ${style.text}`}>
              {status === "pending_approval" && "Cuenta Pendiente de Aprobación"}
              {status === "grace_period" && "¡Periodo de Gracia Activo!"}
              {status === "expiring_soon" && "Tu Suscripción Expira Pronto"}
              {status === "inactive" && "Cuenta Desactivada"}
              {status === "rejected" && "Solicitud Rechazada"}
            </p>
            
            <p className={`text-sm mt-1 ${style.text} opacity-80`}>
              {message}
            </p>
            
            {timeText && (
              <p className={`text-xs mt-2 ${style.text} opacity-70 flex items-center gap-1`}>
                <Clock className="w-3 h-3" />
                {timeText}
              </p>
            )}
          </div>
          
          {style.showUpgrade && (
            <Button 
              onClick={onUpgrade}
              className="flex-shrink-0 bg-primary hover:bg-[#b8c100] text-[#384451] font-semibold"
              data-testid="upgrade-btn"
            >
              {status === "inactive" ? "Reactivar Cuenta" : "Ver Planes"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MembershipBanner;
